import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { shortName } from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import {
  childrenOf,
  companiesOf,
  subtreeCompanyCounts,
  subtreeMaterials,
  unknownSupplierCount,
} from '@/components/value-chain/treeIndex';
import type { ValueChainNode } from '@/types/valueChain';

const PIE_COLORS = ['#93c5fd', '#86efac', '#fcd34d', '#fda4af', '#c4b5fd', '#e5e7eb'];

type Props = {
  node: ValueChainNode;
  index: TreeIndex;
};

export function InsightsRow({ node, index }: Props) {
  const companies = subtreeCompanyCounts(index, node.id).slice(0, 5);
  const materials = subtreeMaterials(index, node.id);
  const materialTotal = materials.reduce((sum, row) => sum + row.count, 0);
  const pieData =
    materials.length > 8
      ? Object.values(
          materials.reduce<Record<string, { name: string; value: number }>>((acc, row) => {
            const name = row.node.type;
            acc[name] = acc[name] ?? { name, value: 0 };
            acc[name].value += row.count;
            return acc;
          }, {}),
        )
      : materials.slice(0, 4).map((row) => ({
          name: shortName(row.node.name),
          value: row.count,
        }));
  if (materials.length > 4 && materials.length <= 8 && materialTotal > 0) {
    pieData.push({
      name: 'Other',
      value: materials.slice(4).reduce((sum, row) => sum + row.count, 0),
    });
  }
  const maxCompany = companies[0]?.count ?? 1;
  const insights = deriveInsights(node, index);

  return (
    <div className="grid min-h-[210px] gap-4 lg:grid-cols-3">
      <section className="rounded-2xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Economic Impact (Selected Node)</h3>
        {companies.length === 0 ? (
          <p className="mt-6 text-sm text-gray-400">No mapped suppliers under this node.</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {companies.map((row) => (
              <div key={row.company.id} className="flex items-center gap-3 text-[13px]">
                <span className="w-36 shrink-0 truncate text-gray-700">
                  {shortName(row.company.name)}
                </span>
                <span className="h-2.5 flex-1 rounded-full bg-gray-100">
                  <span
                    className="block h-2.5 rounded-full bg-blue-400"
                    style={{ width: `${Math.max(8, (row.count / maxCompany) * 100)}%` }}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-[11px] text-gray-400">
          Estimated relative supplier mentions (not $ values)
        </p>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Material Composition ({shortName(node.name)})
        </h3>
        {pieData.length === 0 ? (
          <p className="mt-6 text-sm text-gray-400">
            No material or chemical nodes mapped under this selection yet.
          </p>
        ) : (
          <div className="mt-2 flex items-center gap-3">
            <div className="h-[140px] w-[140px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={36} outerRadius={62} stroke="none">
                    {pieData.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5 text-[12px]">
              {pieData.map((entry, i) => (
                <li key={entry.name} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2 text-gray-600">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="truncate">{entry.name}</span>
                  </span>
                  <span className="text-gray-500">
                    {materialTotal ? Math.round((entry.value / materialTotal) * 100) : 0}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Key Insights</h3>
        <ol className="mt-3 space-y-3">
          {insights.map((insight, i) => (
            <li key={insight} className="flex gap-2.5 text-[12px] leading-relaxed text-gray-600">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[11px] font-medium text-white">
                {i + 1}
              </span>
              <span>{insight}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function deriveInsights(node: ValueChainNode, index: TreeIndex): string[] {
  const insights: string[] = [];
  const kids = childrenOf(index, node.id);
  const top = kids[0];
  if (top) {
    const note = firstSentence(top.notes || top.description);
    insights.push(
      note
        ? `${shortName(top.name)} is the top-ranked child. ${note}`
        : `${shortName(top.name)} is the highest-ranked direct child of ${shortName(node.name)}.`,
    );
  }
  const unknown = unknownSupplierCount(index, node.id);
  if (unknown > 0) {
    insights.push(
      `${unknown} direct child${unknown === 1 ? '' : 'ren'} still have an unknown or undisclosed supplier.`,
    );
  }
  const companies = companiesOf(index, node.id);
  if (companies.length) {
    insights.push(
      `Direct manufacturing links on this node: ${companies.map((item) => shortName(item.name)).slice(0, 3).join(', ')}.`,
    );
  }
  const subtree = index.subtreeSize.get(node.id) ?? 1;
  insights.push(
    `This selection covers ${subtree.toLocaleString()} mapped nodes at depth ${index.depthFromRoot.get(node.id) ?? 0} of ${index.maxDepth}.`,
  );
  return insights.slice(0, 4);
}

function firstSentence(text: string): string {
  return text.split(/(?<=[.!?])\s+/)[0] ?? text;
}
